import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Collapse,
  Drawer,
  List,
  Typography,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
  ButtonBase,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  CreditCardOutlined,
  ConfirmationNumberOutlined,
  PeopleOutline,
  GroupAddOutlined,
  ScheduleOutlined,
  History,
  Build,
  ArrowBack,
  MoreHoriz,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import logoWhite from "../../theme/logo/logo_white.png";

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 72;
const HEADER_HEIGHT = 64;

// Match the title icons in each console; Accounts and Security have no title icon.
const MENU_ITEMS = [
  {
    text: "Accounts",
    path: "/internal/accounts",
    defaultPath: "/internal/accounts/list",
    groupId: "accounts",
    icon: <AccountIcon />,
    children: [
      {
        text: "All accounts",
        path: "/internal/accounts/list",
        icon: <PeopleOutline />,
      },
      {
        text: "Promo codes",
        path: "/internal/accounts/promo-codes",
        icon: <ConfirmationNumberOutlined />,
      },
      {
        text: "Bulk Account Creation",
        path: "/internal/accounts/bulk-creation",
        icon: <GroupAddOutlined />,
      },
    ],
  },
  {
    text: "Subscriptions",
    path: "/internal/subscriptions",
    icon: <CreditCardOutlined />,
  },
  {
    text: "System",
    path: "/internal/audit-logs",
    groupId: "system",
    icon: <SecurityIcon />,
    children: [
      { text: "Audit Logs", path: "/internal/audit-logs", icon: <History /> },
      {
        text: "Cron Jobs",
        path: "/internal/cronjob",
        icon: <ScheduleOutlined />,
      },
      { text: "Security", path: "/internal/security", icon: <SecurityIcon /> },
    ],
  },
  {
    text: "Patching Center",
    path: "/internal/patching-center",
    icon: <Build />,
  },
];
const matchesPage = (pathname, path) =>
  pathname === path || pathname.startsWith(path + "/");

const AdminLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { global_authState } = useContext(AuthContext);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const showExpanded = isMobile || isDrawerOpen;
  const drawerWidth = showExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH;
  const activePage = MENU_ITEMS.find(
    (item) =>
      matchesPage(location.pathname, item.path) ||
      item.children?.some((child) => matchesPage(location.pathname, child.path))
  );
  const activeSubPage = activePage?.children?.find((item) =>
    matchesPage(location.pathname, item.path)
  );
  const [expandedGroups, setExpandedGroups] = useState(() =>
    activePage?.children ? { [activePage.path]: true } : {}
  );
  const username = global_authState?.username || "Admin";

  useEffect(() => {
    setMobileOpen(false);
    setAnchorEl(null);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (activePage?.children) {
      setExpandedGroups((current) => ({ ...current, [activePage.path]: true }));
    }
  }, [location.pathname, activePage]);

  const avatar = (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        color: "primary.main",
        fontSize: "0.9rem",
        fontWeight: 700,
      }}
    >
      {username[0].toUpperCase()}
    </Avatar>
  );
  const accountMenuProps = {
    "aria-label": "Account menu for " + username,
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(anchorEl),
    "aria-controls": anchorEl ? "admin-account-menu" : undefined,
    onClick: (event) => setAnchorEl(event.currentTarget),
  };
  const toggleLabel = isMobile
    ? "Close navigation"
    : isDrawerOpen
      ? "Collapse navigation"
      : "Expand navigation";
  const closeMobileNavigation = (event) => {
    if (
      isMobile &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: showExpanded ? "space-between" : "center",
          px: showExpanded ? 2 : 1,
          gap: 1,
          height: HEADER_HEIGHT,
          flexShrink: 0,
          background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
        }}
      >
        {showExpanded && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Box
              component='img'
              src={logoWhite}
              alt='Tax Savy'
              sx={{ width: 118, display: "block" }}
            />
            <Typography
              variant='caption'
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.18)",
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
              }}
            >
              INTERNAL
            </Typography>
          </Box>
        )}
        <Tooltip title={toggleLabel}>
          <IconButton
            aria-label={toggleLabel}
            aria-expanded={showExpanded}
            onClick={() =>
              isMobile
                ? setMobileOpen(false)
                : setIsDrawerOpen((value) => !value)
            }
            sx={{
              color: "white",
              borderRadius: 2,
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            {isMobile ? (
              <CloseIcon />
            ) : showExpanded ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component='nav'
        aria-label='Admin navigation'
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.25,
          py: 2,
        }}
      >
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
        >
          {MENU_ITEMS.map((item) => {
            const isActive = activePage?.path === item.path;
            const groupOpen = Boolean(expandedGroups[item.path]);
            const groupId = `admin-navigation-${item.groupId}`;
            return (
              <Box component='li' key={item.path} sx={{ listStyle: "none" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 46,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: isActive
                      ? (t) => alpha(t.palette.primary.main, 0.12)
                      : "transparent",
                    bgcolor: isActive
                      ? (t) => alpha(t.palette.primary.main, 0.08)
                      : "transparent",
                    color: isActive ? "primary.main" : "text.secondary",
                    "&:hover": {
                      bgcolor: isActive
                        ? (t) => alpha(t.palette.primary.main, 0.12)
                        : "action.hover",
                      color: "primary.main",
                    },
                  }}
                >
                  <Tooltip
                    title={showExpanded ? "" : item.text}
                    placement='right'
                  >
                    <ListItemButton
                      component={RouterLink}
                      to={item.defaultPath || item.path}
                      selected={isActive}
                      aria-label={item.text}
                      aria-current={
                        isActive && !item.children ? "page" : undefined
                      }
                      onClick={(event) => {
                        if (
                          item.children &&
                          !event.ctrlKey &&
                          !event.metaKey &&
                          !event.shiftKey &&
                          !event.altKey
                        ) {
                          setExpandedGroups((current) => ({
                            ...current,
                            [item.path]: true,
                          }));
                          if (!showExpanded) setIsDrawerOpen(true);
                        }
                        closeMobileNavigation(event);
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 44,
                        px: showExpanded ? 1.5 : 0,
                        pr: showExpanded && item.children ? 0.5 : undefined,
                        borderRadius: 2,
                        justifyContent: showExpanded ? "flex-start" : "center",
                        color: "inherit",
                        "&.Mui-selected, &.Mui-selected:hover, &:hover": {
                          bgcolor: "transparent",
                        },
                        "&.Mui-focusVisible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: showExpanded ? 36 : 0,
                          color: "inherit",
                          justifyContent: showExpanded
                            ? "flex-start"
                            : "center",
                          "& .MuiSvgIcon-root": { fontSize: 22 },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {showExpanded && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            fontWeight: isActive ? 600 : 500,
                            noWrap: true,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                  {item.children && showExpanded && (
                    <IconButton
                      size='small'
                      aria-label={`${groupOpen ? "Collapse" : "Expand"} ${item.text}`}
                      aria-expanded={groupOpen}
                      aria-controls={groupId}
                      onClick={() =>
                        setExpandedGroups((current) => ({
                          ...current,
                          [item.path]: !current[item.path],
                        }))
                      }
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 0.75,
                        flexShrink: 0,
                        borderRadius: 1,
                        color: "inherit",
                        "&:hover": {
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        },
                        "&.Mui-focusVisible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <ChevronRightIcon
                        sx={{
                          fontSize: 18,
                          transform: groupOpen
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: (t) =>
                            t.transitions.create("transform", {
                              duration: t.transitions.duration.shortest,
                            }),
                          "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                          },
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
                {item.children && (
                  <Collapse
                    id={groupId}
                    in={showExpanded && groupOpen}
                    timeout='auto'
                    unmountOnExit
                  >
                    <List
                      aria-label={`${item.text} pages`}
                      disablePadding
                      sx={{
                        mt: 0.5,
                        ml: showExpanded ? 2.5 : 0,
                        pl: showExpanded ? 1 : 0,
                        borderLeft: showExpanded ? 1 : 0,
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {item.children.map((child) => {
                        const selected = activeSubPage?.path === child.path;
                        return (
                          <Box
                            component='li'
                            key={child.path}
                            sx={{ listStyle: "none" }}
                          >
                            <Tooltip
                              title={showExpanded ? "" : child.text}
                              placement='right'
                            >
                              <ListItemButton
                                component={RouterLink}
                                to={child.path}
                                selected={selected}
                                aria-label={child.text}
                                aria-current={selected ? "page" : undefined}
                                onClick={closeMobileNavigation}
                                sx={{
                                  minHeight: 40,
                                  px: showExpanded ? 1.25 : 0,
                                  borderRadius: 1.5,
                                  justifyContent: showExpanded
                                    ? "flex-start"
                                    : "center",
                                  color: selected
                                    ? "primary.main"
                                    : "text.secondary",
                                  "&.Mui-selected": {
                                    bgcolor: (t) =>
                                      alpha(t.palette.primary.main, 0.08),
                                  },
                                  "&.Mui-selected:hover": {
                                    bgcolor: (t) =>
                                      alpha(t.palette.primary.main, 0.12),
                                  },
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                    color: "primary.main",
                                  },
                                  "&.Mui-focusVisible": {
                                    outline: "2px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: -2,
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: showExpanded ? 30 : 0,
                                    color: "inherit",
                                    "& .MuiSvgIcon-root": { fontSize: 19 },
                                  }}
                                >
                                  {child.icon}
                                </ListItemIcon>
                                {showExpanded && (
                                  <ListItemText
                                    primary={child.text}
                                    primaryTypographyProps={{
                                      fontSize: "0.8125rem",
                                      fontWeight: selected ? 600 : 500,
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
      </Box>
      <Box
        sx={{ p: 1.25, borderTop: 1, borderColor: "divider", flexShrink: 0 }}
      >
        <Tooltip title={showExpanded ? "" : username} placement='right'>
          <ButtonBase
            {...accountMenuProps}
            sx={{
              width: "100%",
              minHeight: 52,
              px: showExpanded ? 1 : 0,
              gap: 1.25,
              borderRadius: 2,
              justifyContent: showExpanded ? "flex-start" : "center",
              "&:hover": { bgcolor: "action.hover" },
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "primary.main",
              },
            }}
          >
            {avatar}
            {showExpanded && (
              <>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Typography variant='body2' fontWeight={600} noWrap>
                    {username}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Internal console
                  </Typography>
                </Box>
                <MoreHoriz sx={{ color: "text.secondary", fontSize: 20 }} />
              </>
            )}
          </ButtonBase>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        overflow: "hidden",
        bgcolor: "background.default",
        pt: isMobile ? HEADER_HEIGHT + "px" : 0,
        boxSizing: "border-box",
      }}
    >
      {isMobile && (
        <Box
          component='header'
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            height: HEADER_HEIGHT,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <IconButton
            aria-label='Open navigation'
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
            edge='start'
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant='subtitle1'
            fontWeight={700}
            noWrap
            sx={{ flex: 1 }}
          >
            {activeSubPage?.text || activePage?.text || "Admin Panel"}
          </Typography>
          <IconButton {...accountMenuProps} sx={{ p: 0.5 }}>
            {avatar}
          </IconButton>
        </Box>
      )}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          height: "100%",
          transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.shorter,
          }),
          "& .MuiDrawer-paper": {
            position: isMobile ? "fixed" : "relative",
            width: drawerWidth,
            maxWidth: "100vw",
            height: "100%",
            boxSizing: "border-box",
            borderRight: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            overflow: "hidden",
            transition: theme.transitions.create("width", {
              duration: theme.transitions.duration.shorter,
            }),
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "& .MuiDrawer-paper": { transition: "none" },
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Box
        component='main'
        sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto" }}
      >
        {children}
      </Box>
      <Menu
        id='admin-account-menu'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 200,
            mt: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(30,20,50,0.1)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setMobileOpen(false);
            navigate("/consolePanel");
          }}
          sx={{ mx: 0.5, borderRadius: 1.5 }}
        >
          <ListItemIcon>
            <ArrowBack fontSize='small' />
          </ListItemIcon>
          <ListItemText
            primary='Back to App'
            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminLayout;
